import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import Hls from 'hls.js';
var DEFAULT_PROGRESS_CHECKPOINTS = [25, 50, 75];
export var useHlsVideo = function useHlsVideo(_ref) {
  var videoUrl = _ref.videoUrl,
    _ref$isMuted = _ref.isMuted,
    initialIsMuted = _ref$isMuted === void 0 ? true : _ref$isMuted,
    _ref$progressCheckpoi = _ref.progressCheckpoints,
    progressCheckpoints = _ref$progressCheckpoi === void 0 ? DEFAULT_PROGRESS_CHECKPOINTS : _ref$progressCheckpoi,
    onStart = _ref.onStart,
    onComplete = _ref.onComplete,
    onProgress = _ref.onProgress,
    onError = _ref.onError;
  var videoRef = useRef(null);
  var hlsInstanceRef = useRef(null);
  var firedCheckpointsRef = useRef(new Set());
  var _useState = useState(initialIsMuted),
    _useState2 = _slicedToArray(_useState, 2),
    isMuted = _useState2[0],
    setIsMuted = _useState2[1];
  var getVideoInfo = useCallback(function () {
    var video = videoRef.current;
    if (!video) {
      return {
        currentTime: 0,
        duration: 0,
        progressPercent: 0
      };
    }
    var currentTime = video.currentTime,
      duration = video.duration;
    var progressPercent = duration > 0 ? currentTime / duration * 100 : 0;
    return {
      currentTime: currentTime,
      duration: duration,
      progressPercent: progressPercent
    };
  }, []);
  var handleTimeUpdate = useCallback(function () {
    var video = videoRef.current;
    if (!video || video.duration === 0) {
      return;
    }
    if (!firedCheckpointsRef.current.has('start')) {
      firedCheckpointsRef.current.add('start');
      onStart == null ? void 0 : onStart(getVideoInfo());
    }
    var videoInfo = getVideoInfo();
    var progressPercent = videoInfo.progressPercent,
      currentTime = videoInfo.currentTime,
      duration = videoInfo.duration;
    if (onProgress) {
      progressCheckpoints.forEach(function (checkpoint) {
        if (progressPercent >= checkpoint && !firedCheckpointsRef.current.has(checkpoint)) {
          firedCheckpointsRef.current.add(checkpoint);
          onProgress(videoInfo);
        }
      });
    }
    var completeThresholdInSeconds = 0.5;
    if (onComplete && duration - currentTime <= completeThresholdInSeconds && !firedCheckpointsRef.current.has('complete')) {
      firedCheckpointsRef.current.add('complete');
      onComplete(videoInfo);
    }
  }, [getVideoInfo, onStart, onProgress, onComplete, progressCheckpoints]);
  useEffect(function () {
    var videoElement = videoRef.current;
    if (!videoUrl || !videoElement) {
      return;
    }
    firedCheckpointsRef.current.clear();
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    if (Hls.isSupported()) {
      var hls = new Hls({
        capLevelToPlayerSize: true,
        startLevel: -1
      });
      hlsInstanceRef.current = hls;
      hls.loadSource(videoUrl);
      hls.attachMedia(videoElement);
      hls.on(Hls.Events.ERROR, function (_, data) {
        onError == null ? void 0 : onError(data);
      });
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      videoElement.src = videoUrl;
    }
    return function () {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
      }
    };
  }, [videoUrl, handleTimeUpdate, onError]);
  useEffect(function () {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);
  var controls = useMemo(function () {
    return {
      pause: function pause() {
        var _videoRef$current;
        return (_videoRef$current = videoRef.current) == null ? void 0 : _videoRef$current.pause();
      },
      play: function play() {
        var _videoRef$current2;
        return (_videoRef$current2 = videoRef.current) == null ? void 0 : _videoRef$current2.play()["catch"](function (err) {
          onError == null ? void 0 : onError(err);
        });
      },
      toggleMute: function toggleMute() {
        return setIsMuted(function (prev) {
          return !prev;
        });
      }
    };
  }, [onError]);
  return {
    controls: controls,
    isMuted: isMuted,
    videoRef: videoRef
  };
};